import { useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import type { ClientCaseFilter, DoctorInput, DoctorPatientsFilter } from '@/domain';
import { useClientRepository } from '../container';
import { keys } from '../query-keys';
import type {
  DoctorCasesExport,
  DoctorFilter,
  DoctorPage,
  DoctorPatientsExport,
  MergeDoctorCommand,
  NewPatient,
  PatientFilter,
  PatientPage,
  RenamePatientCommand,
  SaveDoctorCommand,
} from '../ports/ClientRepository';

/**
 * Doctors and patients: the two lists, a record with its patients, its doctors
 * and its cases, and adding, editing, deleting and merging a record. The
 * request of 6 October 2026 (second), items 4 and 5, and the request of
 * 6 October 2026 (third).
 *
 * None of these is optimistic, and none is queued offline. A record is typed
 * at a desk, the server decides whether a name is already taken or a record
 * still has cases, and its words are what the screen shows.
 *
 * The request of 6 October 2026 (fifth) added the counts above each list
 * (`useDoctorSummary`, `usePatientSummary`) and the list narrowed to one of
 * them (`show` on the two filters); a record's cases and a doctor's patients
 * searched, narrowed, ordered and paged by the server, with their exports;
 * and the conversion of the numbers and addresses typed before they had an
 * international form.
 */

/** How many cases a profile shows at once, unless the screen says otherwise. */
export const CLIENT_CASES_PAGE_SIZE = 25;

/** The fewest letters the intake form's doctor field searches on. */
export const DOCTOR_SEARCH_MIN = 2;
/** How many doctors it suggests: a short list under a field, not a page. */
export const DOCTOR_SEARCH_PAGE_SIZE = 8;
/** How long typing has to pause before it asks: the same pause every search box takes. */
export const DOCTOR_SEARCH_DEBOUNCE_MS = 200;

/** The patient field searches as the doctor field does: from two letters, a short list, after the same pause. */
export const PATIENT_SEARCH_MIN = DOCTOR_SEARCH_MIN;
export const PATIENT_SEARCH_PAGE_SIZE = DOCTOR_SEARCH_PAGE_SIZE;
export const PATIENT_SEARCH_DEBOUNCE_MS = DOCTOR_SEARCH_DEBOUNCE_MS;

/** A list is stale after this long: a desk adds a doctor, and the list across the room shows it. */
const LIST_STALE_MS = 15_000;

export function useDoctors(filter: DoctorFilter) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.list(filter),
    queryFn: () => repo.doctors(filter),
    staleTime: LIST_STALE_MS,
    // The page in hand stays on screen while another page, another order or a
    // longer search is fetched, so the table does not blank under the reader.
    placeholderData: (previous) => previous,
  });
}

/**
 * The counts above the list of doctors: all of them, the new this week and
 * this month, the returning, those with active cases, those the conversion
 * left for review, and those still in the old format. The server's, on the
 * lab's calendar. Read again whenever a doctor or a case changes, since it is
 * kept under the doctors' own key.
 */
export function useDoctorSummary() {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.summary,
    queryFn: () => repo.doctorSummary(),
    staleTime: LIST_STALE_MS,
  });
}

export function useDoctor(id: string | null | undefined) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.byId(id ?? ''),
    queryFn: () => repo.doctor(id as string),
    enabled: id != null && id !== '',
  });
}

/** A filter that says nothing about paging: the server then answers every match at once. */
const EVERY_PATIENT: DoctorPatientsFilter = {};

/**
 * The patients with a case under one doctor, each with what its cases with
 * THIS doctor come to (`withDoctor`): the Patients tab of a doctor's page, and
 * what the intake form lists when a doctor is chosen and nothing is typed.
 * Searched, narrowed to those with an active case, ordered and paged by the
 * server as the filter asks; with no filter, every one of them, by name.
 * `data` is always a page: `{ items, page, pageSize, total }`. Asks for
 * nothing until there is a doctor to ask about, and keeps the page in hand
 * while the next is fetched.
 */
export function useDoctorPatients(
  doctorId: string | null | undefined,
  filter: DoctorPatientsFilter = EVERY_PATIENT,
) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.patients(doctorId ?? '', filter),
    queryFn: () => repo.doctorPatients(doctorId as string, filter),
    enabled: doctorId != null && doctorId !== '',
    staleTime: LIST_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/**
 * A page of a doctor's cases: searched, narrowed, ordered and paged by the
 * server as the filter asks, newest booked first when it asks for no order.
 * `data.total` is how many cases match, not how many are on the page. The
 * page in hand stays while another page or another filter is fetched.
 */
export function useDoctorCases(doctorId: string | null | undefined, filter: ClientCaseFilter) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.cases(doctorId ?? '', filter),
    queryFn: () => repo.doctorCases(doctorId as string, filter),
    enabled: doctorId != null && doctorId !== '',
    staleTime: LIST_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/**
 * A doctor's cases as a file: every case the filter on screen matches, in its
 * order, as CSV. A mutation, as the Archive's export is, resolving to the
 * bytes: the screen hands them to the device. Paging in the filter is not
 * sent. Never asked about first: an export changes nothing.
 */
export function useExportDoctorCases() {
  const repo = useClientRepository();
  return useMutation({
    mutationFn: (command: DoctorCasesExport) => repo.exportDoctorCases(command),
  });
}

/** A doctor's patients as a file, likewise. */
export function useExportDoctorPatients() {
  const repo = useClientRepository();
  return useMutation({
    mutationFn: (command: DoctorPatientsExport) => repo.exportDoctorPatients(command),
  });
}

/**
 * What converting the old telephone numbers and addresses would do: every
 * doctor with something waiting, the old text, and what it becomes or why it
 * is left for review. Reading it changes nothing. Asked only when `enabled`,
 * since only management may read it and the sheet that shows it is opened on
 * purpose; never kept stale, because it is read to decide.
 */
export function useContactConversion(enabled: boolean) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.conversion,
    queryFn: () => repo.contactConversion(),
    enabled,
    staleTime: 0,
  });
}

/**
 * A doctor's possible duplicates: the other doctors whose name is this one's
 * once titles and punctuation are set aside. The request of 6 October 2026
 * (third), item 1. An empty list for most doctors, and what the profile's
 * Possible duplicates block is drawn from. Asks for nothing without a doctor.
 */
export function useSimilarDoctors(doctorId: string | null | undefined) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.doctors.similar(doctorId ?? ''),
    queryFn: () => repo.similarDoctors(doctorId as string),
    enabled: doctorId != null && doctorId !== '',
    staleTime: LIST_STALE_MS,
  });
}

export function usePatients(filter: PatientFilter) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.patients.list(filter),
    queryFn: () => repo.patients(filter),
    staleTime: LIST_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/** The counts above the list of patients: all, new this week and this month, and with active cases. */
export function usePatientSummary() {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.patients.summary,
    queryFn: () => repo.patientSummary(),
    staleTime: LIST_STALE_MS,
  });
}

export function usePatient(id: string | null | undefined) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.patients.byId(id ?? ''),
    queryFn: () => repo.patient(id as string),
    enabled: id != null && id !== '',
  });
}

/** A page of a patient's cases, as a doctor's are read: the same filter, with `doctorId` for one doctor's. */
export function usePatientCases(patientId: string | null | undefined, filter: ClientCaseFilter) {
  const repo = useClientRepository();
  return useQuery({
    queryKey: keys.patients.cases(patientId ?? '', filter),
    queryFn: () => repo.patientCases(patientId as string, filter),
    enabled: patientId != null && patientId !== '',
    staleTime: LIST_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/**
 * A value that follows another a moment later, once it has stopped changing.
 * Clearing is not delayed. The shared hook of the same name belongs to the
 * screens, which this layer may not import, so the search below keeps its own.
 */
function useSettled(value: string, delayMs: number): string {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    if (value === '') {
      setSettled('');
      return;
    }
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return value === '' ? '' : settled;
}

/** A page of suggestions as it is cached: with the text it is the answer to. */
interface Answered<Page> {
  text: string;
  page: Page;
}

/**
 * The search behind a field that suggests records while a name is typed.
 *
 * Asked only once enough letters are in and the typing has paused, so a
 * keystroke is not a request. `data` is undefined until then, and again
 * whenever the field is cleared or cut below the least it searches on: nothing
 * typed suggests nothing. While a longer text is being asked for, the matches
 * of the shorter one stay.
 *
 * `answeredFor` is the text `data` is the answer to, trimmed as it was sent,
 * and empty when there is no `data`. A screen that has to say something about
 * exactly what is in the field, that nobody has this name or that one patient
 * does, compares it with the field: while they differ, `data` is the answer to
 * an earlier text, and the screen waits. Kept with the answer in the cache and
 * not worked out beside it, so it is right for the matches that stay on screen
 * during a longer search as well.
 */
function useSuggestions<Page>(
  text: string,
  least: number,
  delayMs: number,
  keyOf: (settled: string) => QueryKey,
  ask: (settled: string) => Promise<Page>,
) {
  const typed = text.trim();
  const settled = useSettled(typed.length >= least ? typed : '', delayMs);
  const enabled = settled !== '';

  const query = useQuery({
    queryKey: keyOf(settled),
    queryFn: async (): Promise<Answered<Page>> => ({ text: settled, page: await ask(settled) }),
    enabled,
    staleTime: LIST_STALE_MS,
    placeholderData: (previous) => (enabled ? previous : undefined),
  });

  return { ...query, data: query.data?.page, answeredFor: query.data?.text ?? '' };
}

/**
 * The doctors the intake form suggests while a name is typed: a few, by name,
 * matching what was typed in the name, the clinic, the mobile, the email or
 * the ID. `data` is the page of them and `answeredFor` the text it answers:
 * see `useSuggestions`.
 */
export function useDoctorSearch(text: string) {
  const repo = useClientRepository();
  return useSuggestions<DoctorPage>(
    text,
    DOCTOR_SEARCH_MIN,
    DOCTOR_SEARCH_DEBOUNCE_MS,
    keys.doctors.search,
    (settled) =>
      repo.doctors({ q: settled, sort: 'name', page: 1, pageSize: DOCTOR_SEARCH_PAGE_SIZE }),
  );
}

/**
 * The patients the intake form suggests while a name is typed: a few, by
 * name, out of EVERY patient and not one doctor's, matching what was typed in
 * the name or the ID. The request of 6 October 2026 (third), item 3. Shaped as
 * the doctor search is, `answeredFor` included.
 *
 * A short page, so `data.total` may be more than `data.items` holds: a screen
 * that needs every patient of exactly one name is sure of it only when the
 * page holds them all.
 */
export function usePatientSearch(text: string) {
  const repo = useClientRepository();
  return useSuggestions<PatientPage>(
    text,
    PATIENT_SEARCH_MIN,
    PATIENT_SEARCH_DEBOUNCE_MS,
    keys.patients.search,
    (settled) =>
      repo.patients({ q: settled, sort: 'name', page: 1, pageSize: PATIENT_SEARCH_PAGE_SIZE }),
  );
}

/** A name is on every case of the record, so a new one is on every list and sheet that shows a case. */
function refreshCases(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: keys.orders.all });
  void queryClient.invalidateQueries({ queryKey: keys.kits.all });
  void queryClient.invalidateQueries({ queryKey: keys.board() });
  void queryClient.invalidateQueries({ queryKey: ['archive'] });
  void queryClient.invalidateQueries({ queryKey: keys.printAll });
}

/** Every one of these writes a row to the log. */
function refreshLog(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['activity'] });
}

/**
 * A doctor's address feeds what the address form suggests: the areas of a
 * city, and a city typed by hand because the list did not have it. So the
 * searches are read again after any write that can change an address.
 */
function refreshPlaces(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: keys.geo.places });
}

/**
 * Every list of doctors and every doctor's record but one that is gone. What
 * is cached of that one is marked stale and NOT asked for again: a profile
 * still on screen for the moment before the screen leaves it would otherwise
 * turn into the server's not found. The others are read again because a doctor
 * who has gone is no longer anybody's possible duplicate.
 */
function refreshDoctorsWithout(queryClient: QueryClient, goneId: string): void {
  void queryClient.invalidateQueries({ queryKey: keys.doctors.byId(goneId), refetchType: 'none' });
  void queryClient.invalidateQueries({ queryKey: keys.doctors.lists });
  refreshDoctorCounts(queryClient);
  void queryClient.invalidateQueries({
    queryKey: keys.doctors.details,
    predicate: (query) => query.queryKey[2] !== goneId,
  });
}

/** The counts above the list of doctors, and what is still waiting to be converted. */
function refreshDoctorCounts(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: keys.doctors.summary });
  void queryClient.invalidateQueries({ queryKey: keys.doctors.conversion });
}

export function useAddDoctor() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doctor: DoctorInput) => repo.addDoctor(doctor),
    onSuccess: (doctor) => {
      queryClient.setQueryData(keys.doctors.byId(doctor.id), doctor);
      void queryClient.invalidateQueries({ queryKey: keys.doctors.lists });
      // One more doctor, and one more made this week.
      refreshDoctorCounts(queryClient);
      // A doctor typed a second time is the first one's possible duplicate.
      void queryClient.invalidateQueries({
        queryKey: keys.doctors.details,
        predicate: (query) => query.queryKey[3] === 'similar',
      });
      refreshPlaces(queryClient);
      refreshLog(queryClient);
    },
  });
}

/**
 * Saving an edit to a doctor. The record on screen takes the answer at once;
 * the lists of doctors and of patients, whose rows name the doctor and the
 * clinic, are read again, and with them the counts above the list, which a
 * saved number or address takes a doctor out of. A new NAME also changes
 * every case of the doctor, so the orders, the kits, the board, the archive
 * and the printed sheets are read again with it. So does a new clinic, number
 * or address, which every case of the doctor carries as where it is delivered
 * (`deliverTo`). An edit to the email or the website alone leaves them be.
 */
export function useSaveDoctor() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: SaveDoctorCommand) => repo.saveDoctor(command),
    onSuccess: (doctor, command) => {
      queryClient.setQueryData(keys.doctors.byId(doctor.id), doctor);
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
      const { name, clinic, phone, address } = command.changes;
      if ([name, clinic, phone, address].some((sent) => sent !== undefined)) {
        refreshCases(queryClient);
      }
      if (address !== undefined) refreshPlaces(queryClient);
      refreshLog(queryClient);
    },
  });
}

/**
 * Deleting a doctor. The lists and the other doctors' records are read again;
 * the record itself is not: see `refreshDoctorsWithout`. No patient changes
 * with it: a doctor that can be deleted has no case, and so is no patient's
 * doctor.
 */
export function useDeleteDoctor() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repo.deleteDoctor(id),
    onSuccess: (_nothing, id) => {
      refreshDoctorsWithout(queryClient, id);
      refreshLog(queryClient);
    },
  });
}

/**
 * Merging one doctor into another. The request of 6 October 2026 (third),
 * item 1. The record of the doctor kept takes the answer at once. Every case
 * of the duplicate now reads the kept doctor's name, so the orders, the kits,
 * the board, the archive and the printed sheets are read again, with the
 * patients, whose records name their doctors, and every list and record of a
 * doctor but the duplicate's own, which is gone.
 */
export function useMergeDoctor() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: MergeDoctorCommand) => repo.mergeDoctor(command),
    onSuccess: (doctor, command) => {
      queryClient.setQueryData(keys.doctors.byId(doctor.id), doctor);
      refreshDoctorsWithout(queryClient, command.duplicateId);
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
      refreshCases(queryClient);
      refreshPlaces(queryClient);
      refreshLog(queryClient);
    },
  });
}

/**
 * Converting the old telephone numbers and addresses: exactly what
 * `useContactConversion` listed, done. The request of 6 October 2026 (fifth),
 * section 2. It resolves to what was done, in the list's own shape. Every
 * doctor may have changed, so every list and record of a doctor is read again
 * with the counts above the list; the cases too, which carry where each is
 * delivered; and what the address form suggests.
 */
export function useConvertContacts() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => repo.convertContacts(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      refreshCases(queryClient);
      refreshPlaces(queryClient);
      refreshLog(queryClient);
    },
  });
}

/**
 * Adding a patient: a name, and no doctor. Nothing of any doctor changes, so
 * only the lists of patients are read again.
 */
export function useAddPatient() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patient: NewPatient) => repo.addPatient(patient),
    onSuccess: (patient) => {
      queryClient.setQueryData(keys.patients.byId(patient.id), patient);
      void queryClient.invalidateQueries({ queryKey: keys.patients.lists });
      // One more patient, and one more made this week.
      void queryClient.invalidateQueries({ queryKey: keys.patients.summary });
      refreshLog(queryClient);
    },
  });
}

/**
 * Renaming a patient. The name is on every case of the patient, so the orders,
 * the kits, the board, the archive and the printed sheets are read again, with
 * the lists of patients and each doctor's own list of patients.
 */
export function useRenamePatient() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: RenamePatientCommand) => repo.renamePatient(command),
    onSuccess: (patient) => {
      queryClient.setQueryData(keys.patients.byId(patient.id), patient);
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      refreshCases(queryClient);
      refreshLog(queryClient);
    },
  });
}

/**
 * Deleting a patient. The lists are read again and the record itself is not,
 * as with a doctor. A patient that can be deleted has no case, so no doctor's
 * list of patients held it.
 */
export function useDeletePatient() {
  const repo = useClientRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repo.deletePatient(id),
    onSuccess: (_nothing, id) => {
      void queryClient.invalidateQueries({ queryKey: keys.patients.byId(id), refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: keys.patients.lists });
      void queryClient.invalidateQueries({ queryKey: keys.patients.summary });
      refreshLog(queryClient);
    },
  });
}
